import React from "react";
import { Pricing, money } from "../business-case/calculator";
import { Section, SectionHeading, SectionLabel } from "../ui";

type Plan = { key: string; name: string; price: string; scope: string; note: string };

function plans(pricing: Pricing): Plan[] {
  const pilot: Plan = {
    key: pricing.pilot.key,
    name: pricing.pilot.name,
    price: money(pricing.pilot.price),
    scope: `Up to ${pricing.pilot.maxParticipants} participants`,
    note: "Standard setup included",
  };

  const annual = pricing.annualTiers.map((tier) => ({
    key: tier.key,
    name: tier.planName,
    price: tier.annualPrice === null ? "Custom" : money(tier.annualPrice),
    scope:
      tier.annualPrice === null
        ? "Built around your scale"
        : `${money(tier.annualPrice + pricing.setupFee)} first year`,
    note:
      tier.annualPrice === null
        ? "Scope confirmed with FutureLab"
        : `Includes ${money(pricing.setupFee)} setup`,
  }));

  return [pilot, ...annual];
}

export default function PlanTable({ pricing }: { pricing: Pricing }) {
  return (
    <Section id="plans" className="tw-scroll-mt-24 tw-bg-[#10233f] tw-text-white">
      <div className="tw-grid tw-grid-cols-2 tw-gap-14 sm:tw-grid-cols-1 sm:tw-gap-5">
        <div>
          <SectionLabel tone="mint">Platform pricing</SectionLabel>
          <SectionHeading>Start with a pilot or scale through an annual plan.</SectionHeading>
          <p className="tw-mt-4 tw-mb-0 tw-text-sm tw-font-bold tw-text-[#55cfc1]">
            All prices are in US dollars.
          </p>
        </div>
        <p className="tw-m-0 tw-self-center tw-text-lg tw-leading-8 tw-text-[#c3d5e6] sm:tw-self-start">
          Mentors and mentees are counted together. Administrator accounts are separate and do not
          reduce your participant allowance.
        </p>
      </div>

      {/* One divided panel rather than four cards: these are rungs of a single ladder, and
          detached cards read as four unrelated offers. */}
      <div className="tw-mt-14 tw-grid tw-grid-cols-4 tw-divide-x tw-divide-white/15 tw-overflow-hidden tw-rounded-[24px] tw-border tw-border-white/15 sm:tw-mt-8 sm:tw-grid-cols-1 sm:tw-divide-x-0 sm:tw-divide-y">
        {plans(pricing).map((plan) => (
          <article key={plan.key} className="tw-p-7 sm:tw-p-6">
            <h3 className="tw-m-0 tw-text-sm tw-font-bold tw-text-white">{plan.name}</h3>
            <p className="tw-mt-3 tw-mb-0 tw-text-4xl tw-font-black tw-tracking-[-.045em] tw-text-white sm:tw-text-3xl">
              {plan.price}
            </p>
            <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-font-bold tw-text-[#55cfc1]">{plan.scope}</p>
            <p className="tw-mt-6 tw-mb-0 tw-border-t tw-border-white/15 tw-pt-5 tw-text-sm tw-text-[#a9c0d6]">
              {plan.note}
            </p>
          </article>
        ))}
      </div>
    </Section>
  );
}
